// Build trigger: 2026-01-01T11:38
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { FC, ReactNode, Suspense, useMemo } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

import { queryClient } from "@/lib/queryClient";
import { RPCProviderProvider, useRPCProviderContext } from "@/contexts/RPCProviderContext";
import { WebSocketProvider } from "@/providers/WebSocketProvider";
import { MarketDataProvider } from "@/providers/MarketDataProvider";
import { ActivityFeedProvider } from "@/providers/ActivityFeedProvider";
import { VisibilityReconnectProvider } from "@/providers/VisibilityReconnectProvider";
import { NewCoinRealtimeProvider } from "@/providers/NewCoinRealtimeProvider";
import AppShell from "@/components/layout/AppShell";

import { ConnectionProvider, WalletProvider as SolanaWalletProvider } from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import '@solana/wallet-adapter-react-ui/styles.css';

import { UsernameSetupProvider } from "@/components/username/UsernameSetupProvider";
import { HowItWorksModal } from "@/components/onboarding/HowItWorksModal";
import { useSentryNavigation } from "@/hooks/useSentryNavigation";
import { useSessionManager } from "@/hooks/useSessionManager";
import { useScrollRestoration } from "@/hooks/useScrollRestoration";
import { RequireAdmin } from "@/components/auth/RequireAdmin";
import { SessionExpiryProvider } from "@/providers/SessionExpiryProvider";
import { TransactionDialogProvider } from "@/contexts/TransactionDialogContext";
import { TransactionProgressDialog } from "@/components/transactions/TransactionProgressDialog";

// Lazy-loaded page components
const Index = lazyWithRetry(() => import("@/pages/Index"));
const Tokens = lazyWithRetry(() => import("@/pages/Tokens"));
const TokenDetail = lazyWithRetry(() => import("@/pages/TokenDetail"));
const Watchlist = lazyWithRetry(() => import("@/pages/Watchlist"));
const ListDetail = lazyWithRetry(() => import("@/pages/ListDetail"));
const Feed = lazyWithRetry(() => import("@/pages/Feed"));
const Following = lazyWithRetry(() => import("@/pages/Following"));
const Notifications = lazyWithRetry(() => import("@/pages/Notifications"));
const CreatorProfile = lazyWithRetry(() => import("@/pages/CreatorProfile"));
const CreateCoin = lazyWithRetry(() => import("@/pages/CreateCoin"));
const CreatorFeesRedirect = lazyWithRetry(() => import("@/pages/CreatorFeesRedirect"));
const CreatorFollowers = lazyWithRetry(() => import("@/pages/CreatorFollowers"));
const CreatorFollowing = lazyWithRetry(() => import("@/pages/CreatorFollowing"));
const CreatorRewards = lazyWithRetry(() => import("@/pages/CreatorRewards"));
const AdminDbcFees = lazyWithRetry(() => import("@/pages/AdminDbcFees"));
const AdminTreasury = lazyWithRetry(() => import("@/pages/AdminTreasury"));
const AdminVerifications = lazyWithRetry(() => import("@/pages/AdminVerifications"));
const Portfolio = lazyWithRetry(() => import("@/pages/Portfolio"));
const UserNotFound = lazyWithRetry(() => import("@/pages/UserNotFound"));
const NotFound = lazyWithRetry(() => import("@/pages/NotFound"));
const AuthTurnkey = lazyWithRetry(() => import("@/pages/AuthTurnkey"));
const AuthDiagnostics = lazyWithRetry(() => import("@/pages/AuthDiagnostics"));
const AuthXCallback = lazyWithRetry(() => import("@/pages/AuthXCallback"));
const Settings = lazyWithRetry(() => import("@/pages/Settings"));
const Verified = lazyWithRetry(() => import("@/pages/Verified"));
const Leaderboards = lazyWithRetry(() => import("@/pages/Leaderboards"));
const HowItWorks = lazyWithRetry(() => import("@/pages/HowItWorks"));
const Creators = lazyWithRetry(() => import("@/pages/Creators"));
const Events = lazyWithRetry(() => import("@/pages/Events"));
const Alerts = lazyWithRetry(() => import("@/pages/Alerts"));
const Activity = lazyWithRetry(() => import("@/pages/Activity"));
const ApplyVerification = lazyWithRetry(() => import("@/pages/ApplyVerification"));
const Why = lazyWithRetry(() => import("@/pages/Why"));
const Profile = lazyWithRetry(() => import("@/pages/Profile"));
const MarketMaker = lazyWithRetry(() => import("@/pages/MarketMaker"));
const MarketMakerList = lazyWithRetry(() => import("@/pages/MarketMakerList"));

// Component to track navigation for Sentry breadcrumbs
const NavigationTracker = () => {
  useSentryNavigation();
  return null;
};

// Component to manage session lifecycle (iOS Safari persistence)
const SessionManager = () => {
  useSessionManager();
  return null;
};

// Component to restore scroll position on back/forward navigation
const ScrollRestorer = () => {
  useScrollRestoration();
  return null;
};

// Loading fallback for lazy-loaded routes
const PageLoader = () => (
  <>
    <style>{`
      .animate-fill-up {
        animation: fillUp 0.8s ease-in-out infinite;
      }
      @keyframes fillUp {
        0% { clip-path: inset(100% 0 0 0); }
        100% { clip-path: inset(0 0 0 0); }
      }
      @media (prefers-reduced-motion: reduce) {
        .animate-fill-up { animation: none; clip-path: none; }
      }
    `}</style>
    <div className="flex items-center justify-center min-h-screen bg-black">
      <div className="relative w-16 h-16">
        <img
          src="/logomark.svg"
          alt=""
          className="absolute inset-0 w-16 h-16 opacity-20"
        />
        <img
          src="/logomark.svg"
          alt="Loading"
          className="absolute inset-0 w-16 h-16 animate-fill-up"
        />
      </div>
    </div>
  </>
);

/**
 * Bridge component that mounts Solana wallet adapter providers using the
 * existing RPC context. This allows components like RequireAdmin to call
 * useWallet() without a double-nested RPCProviderProvider.
 * autoConnect=false: admin connects external wallet manually when needed.
 */
const SolanaAdapterBridge: FC<{ children: ReactNode }> = ({ children }) => {
  const { httpEndpoint, wsEndpoint } = useRPCProviderContext();
  const wallets = useMemo(() => [], []);

  return (
    <ConnectionProvider endpoint={httpEndpoint} config={{ wsEndpoint, commitment: 'confirmed' }}>
      <SolanaWalletProvider wallets={wallets} autoConnect={false}>
        <WalletModalProvider>{children}</WalletModalProvider>
      </SolanaWalletProvider>
    </ConnectionProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <SessionManager />
    <TooltipProvider>
      <RPCProviderProvider>
        <SolanaAdapterBridge>
        <WebSocketProvider>
        <MarketDataProvider>
        <ActivityFeedProvider>
        <VisibilityReconnectProvider>
        <NewCoinRealtimeProvider>
        <Sonner />
        <BrowserRouter>
          <SessionExpiryProvider>
            <TransactionDialogProvider>
            <NavigationTracker />
            <ScrollRestorer />
            <HowItWorksModal />
            <TransactionProgressDialog />
            <UsernameSetupProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Auth routes - outside AppShell */}
                <Route path="/auth/turnkey" element={<AuthTurnkey />} />
                <Route path="/auth/diagnostics" element={<AuthDiagnostics />} />
                <Route path="/auth/x/callback" element={<AuthXCallback />} />

                <Route element={<AppShell />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/tokens" element={<Tokens />} />
                  <Route path="/tokens/:slug" element={<TokenDetail />} />
                  <Route path="/token/:mintAddress" element={<TokenDetail />} />
                  {/* Watchlist - redirect /lists to /watchlist for backwards compatibility */}
                  <Route path="/lists" element={<Navigate to="/watchlist" replace />} />
                  <Route path="/watchlist" element={<Watchlist />} />
                  <Route path="/lists/:slug" element={<ListDetail />} />
                  <Route path="/feed" element={<Feed />} />
                  <Route path="/following" element={<Following />} />
                  <Route path="/notifications" element={<Notifications />} />
                  {/* Creator routes - specific paths must come before /:identifier to match correctly */}
                  <Route path="/creator/fees" element={<CreatorFeesRedirect />} />
                  <Route path="/creator/:identifier/followers" element={<CreatorFollowers />} />
                  <Route path="/creator/:identifier/following" element={<CreatorFollowing />} />
                  <Route path="/creator/:identifier/rewards" element={<CreatorRewards />} />
                  {/* Support both wallet address and username */}
                  <Route path="/creator/:identifier" element={<CreatorProfile />} />
                  <Route path="/create" element={<CreateCoin />} />
                  <Route path="/admin/dbc-fees" element={<RequireAdmin><AdminDbcFees /></RequireAdmin>} />
                  <Route path="/admin/treasury" element={<RequireAdmin><AdminTreasury /></RequireAdmin>} />
                  <Route path="/admin/verifications" element={<RequireAdmin><AdminVerifications /></RequireAdmin>} />
                  {/* Portfolio routes */}
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/portfolio/:username" element={<Portfolio />} />
                  {/* Profile */}
                  <Route path="/profile" element={<Profile />} />
                  {/* Settings */}
                  <Route path="/settings" element={<Settings />} />
                  {/* Verified tokens info page */}
                  <Route path="/verified" element={<Verified />} />
                  {/* Leaderboards */}
                  <Route path="/leaderboards" element={<Leaderboards />} />
                  {/* Price Alerts */}
                  <Route path="/alerts" element={<Alerts />} />
                  {/* Activity Feed */}
                  <Route path="/activity" element={<Activity />} />
                  {/* How It Works */}
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  {/* Creator Partnership Program (cold outreach destination) */}
                  <Route path="/creators" element={<Creators />} />
                  {/* Events */}
                  <Route path="/events" element={<Events />} />
                  <Route path="/events/genesis-race" element={<Navigate to="/events" replace />} />
                  {/* Why dextoro */}
                  <Route path="/why" element={<Why />} />
                  {/* Market Maker list + per-coin detail */}
                  <Route path="/market-maker" element={<MarketMakerList />} />
                  <Route path="/mm/:mintAddress" element={<MarketMaker />} />
                  <Route path="/verify" element={<ApplyVerification />} />
                  {/* User not found */}
                  <Route path="/user-not-found" element={<UserNotFound />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </UsernameSetupProvider>
          </TransactionDialogProvider>
          </SessionExpiryProvider>
        </BrowserRouter>
      </NewCoinRealtimeProvider>
      </VisibilityReconnectProvider>
      </ActivityFeedProvider>
      </MarketDataProvider>
      </WebSocketProvider>
      </SolanaAdapterBridge>
      </RPCProviderProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
